import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import React from 'react';
import { GOT_TOURNAMENT_REBUY } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import GameContext from 'contexts/GameContext';
import Decimal from 'decimal.js';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import {
  CONNECTION_LOST_TITLE,
  connectionLostMessage,
  isBuyInConnectionLost
} from 'utils/connectionLost';
import { decompressData, numberFormatter } from 'utils/functions';

import GameModal, {
  primaryBtnStyle,
  secondaryBtnStyle,
  btnRowStyle
} from 'components/GameModal/GameModal';

const messageStyle: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.5,
  textAlign: 'center'
};

const RebuyChipsModal = () => {
  const { tournamentRebuy } = useGameService();
  const { canRebuyChips } = useContext(GameContext);
  const rebuyPrice = useTableSelector((table) => table.game.rebuyPrice);
  const rebuyChips = useTableSelector((table) => table.game.rebuyChips);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const gameID = useTableSelector((table) => table.game.gameID);
  const entryFee = useTableSelector((table) => table.game.entryFee);
  const bountyAmount = useTableSelector((table) => table.game.bountyAmount);
  const gameType = useTableSelector((table) => table.game.gameType);
  const tournamentId = useTableSelector((table) => table.game.tournamentId);
  const [msgTitle, setMsgTitle] = useState('');
  const [msg, setMsg] = useState('');
  const { openRebuyChips, setOpenRebuyChips } = useContext(GameContext);
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);

  const { socket, actionPathStatus, connectionStatus } = useContext(SocketContext);

  // The engine's `gotTnmRebuy` result is pushed to the player's socket with no
  // table/tournament identifier, so EVERY open table's RebuyChipsModal hears
  // it. In multi-table (grid) view that made the "successfully reentered"
  // success modal pop on every cell. Only the table that actually initiated
  // the re-entry should surface the result, so we latch a per-cell flag when
  // this modal fires the rebuy and gate the result handler on it.
  const awaitingRebuyResultRef = useRef(false);

  const handleRebuyChips = () => {
    // EN-11307: the re-entry is a volatile emit, dropped while the action
    // socket is down (or dead and not yet noticed: see isBuyInConnectionLost).
    // Say so on this modal instead of closing it on nothing; its X dismisses,
    // and Re-Entry can be tried again once reconnected.
    if (isBuyInConnectionLost(actionPathStatus, connectionStatus)) {
      setMsgTitle(CONNECTION_LOST_TITLE);
      setMsg(connectionLostMessage('re-enter'));
      return;
    }
    setOpenRebuyChips(false);
    setMsgTitle('');
    setMsg('');
    awaitingRebuyResultRef.current = true;
    tournamentRebuy(seatId, gameID, tournamentId);
  };

  const cancelAddChips = () => {
    setMsgTitle('');
    setMsg('');
    setOpenRebuyChips(false);
  };

  useEffect(() => {
    if (!openRebuyChips) {
      setMsgTitle('');
      setMsg('');
    }
  }, [openRebuyChips]);

  useEffect(() => {
    if (!socket) return;

    const handleRebuyResult = (data: any) => {
      // Ignore results for re-entries initiated on OTHER tables — only the cell
      // that fired this rebuy should show the success/failure modal.
      if (!awaitingRebuyResultRef.current) return;
      awaitingRebuyResultRef.current = false;

      data = decompressData(data);
      if (data) {
        setOpenRebuyChips(true);
        setMsgTitle(data?.title);
        setMsg(data?.msg);
      } else {
        setMsgTitle('');
        setMsg('');
        setOpenRebuyChips(false);
      }
    };

    socket.on(GOT_TOURNAMENT_REBUY, handleRebuyResult);
    return () => {
      socket.off(GOT_TOURNAMENT_REBUY, handleRebuyResult);
    };
  }, [socket, setOpenRebuyChips]);

  const modalTitle = msgTitle !== '' ? msgTitle : 'Still want to play?';

  const defaultMsg = `You can still re-entry. Please note that proceeding with this action will incur a $${Decimal.sum(
    rebuyPrice === null ? 0 : rebuyPrice,
    entryFee === null ? 0 : entryFee,
    gameType === 'Bounty' ? bountyAmount : 0
  )
    .toDP(2)
    .toNumber()} charge, and you will receive ${numberFormatter(
    rebuyChips
  )} chips, and continue playing to the tournament.`;

  return (
    // Tapping outside dismisses, like every other modal: `cancelAddChips` only
    // clears local state, so a dismiss is a pure cancel — the re-entry charge
    // lives in `handleRebuyChips`/`tournamentRebuy()`, behind the Proceed button.
    <GameModal
      open={(canRebuyChips && openRebuyChips) || openRebuyChips}
      onClose={cancelAddChips}
      title={modalTitle}>
      <div style={messageStyle}>
        {msg !== '' ? msg : defaultMsg}
      </div>

      {/* Pre-action prompt: Cancel + Proceed.
          Success result: keep the friendly "Take your seat, Partner" CTA.
          Failed result: no button — the floating X is the dismiss action.    */}
      {msgTitle === '' ? (
        <div style={btnRowStyle}>
          <button style={secondaryBtnStyle} onClick={cancelAddChips}>
            Cancel
          </button>
          <button
            style={{
              ...primaryBtnStyle,
              ...(isMaintenance ? { opacity: 0.4, pointerEvents: 'none' as const } : {})
            }}
            onClick={handleRebuyChips}
            disabled={isMaintenance}
          >
            Proceed
          </button>
        </div>
      ) : !msgTitle.includes('Failed') ? (
        <div style={btnRowStyle}>
          <button style={secondaryBtnStyle} onClick={cancelAddChips}>
            Take your seat, Partner
          </button>
        </div>
      ) : null}
    </GameModal>
  );
};

export default RebuyChipsModal;
